import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { API, Field, LoadError, Loading, SelectField, Welcome, errText, formValues, useHeaders, useLoad, usePortal, useSubmit } from '../../components/clientportal/kit';
import { PhotoPanel } from '../../components/clientportal/PhotoPanel';
import { PortalContacts } from '../../components/clientportal/PortalContacts';

const PasswordForm = () => {
  const headers = useHeaders();
  const { toast, closeDrawer } = usePortal();
  const [busy, run] = useSubmit();
  const submit = (e) => {
    e.preventDefault();
    const f = formValues(e);
    if (f.new_password !== f.confirm) return toast('The new passwords don’t match.');
    return run(async () => {
      try { await axios.post(`${API}/api/account/me/password`, { current_password: f.current_password, new_password: f.new_password }, { headers }); toast('Password changed.'); closeDrawer(); }
      catch (err) { toast(errText(err)); }
    });
  };
  return (
    <form onSubmit={submit} data-testid="password-form">
      <Field label="Current password" name="current_password" type="password" required autoComplete="current-password" />
      <Field label="New password" name="new_password" type="password" required minLength={8} autoComplete="new-password" />
      <Field label="Confirm new password" name="confirm" type="password" required minLength={8} autoComplete="new-password" />
      <p className="muted">Forgot it? <Link to="/forgot-password" className="text-button">Reset by email</Link></p>
      <button className="primary" type="submit" disabled={busy} data-testid="password-save">{busy ? 'Saving…' : 'Change password'}</button>
    </form>
  );
};

const DetailsForm = ({ me, onSaved }) => {
  const headers = useHeaders();
  const { toast } = usePortal();
  const { updateUserLocal } = useAuth();
  const [busy, run] = useSubmit();
  const submit = (e) => {
    e.preventDefault();
    const f = formValues(e);
    run(async () => {
      try {
        const r = await axios.put(`${API}/api/account/me`, { full_name: f.full_name.trim(), phone: f.phone.trim(), preferred_contact: f.preferred_contact }, { headers });
        updateUserLocal({ full_name: r.data.full_name, phone: r.data.phone }); toast('Your details are saved.'); onSaved();
      } catch (err) { toast(errText(err)); }
    });
  };
  return (
    <section className="panel"><h2>Your details</h2>
      <form onSubmit={submit} style={{ marginTop: 22 }} data-testid="account-form">
        <Field label="Full name" name="full_name" defaultValue={me.full_name || ''} required maxLength={100} />
        <Field label="Email" name="email" type="email" defaultValue={me.email} readOnly />
        <p className="muted">To change your sign-in email, message your team so we can verify it’s you.</p>
        <Field label="Phone" name="phone" type="tel" defaultValue={me.phone || ''} required maxLength={30} pattern="[+0-9() .\-]{7,30}" />
        <SelectField label="Preferred contact" name="preferred_contact" defaultValue={me.preferred_contact || 'text'} options={[['text', 'Text'], ['phone', 'Call'], ['email', 'Email']]} />
        <p className="muted">Contact preference does not grant marketing consent.</p>
        <button className="primary" type="submit" disabled={busy} data-testid="account-save">{busy ? 'Saving…' : 'Save details'}</button>
      </form>
    </section>
  );
};

const UpdatesPanel = () => {
  const headers = useHeaders();
  const { toast } = usePortal();
  const [prefs, reload] = useLoad('/api/notifications/preferences');
  const [saving, setSaving] = useState(false);
  const toggle = async (e) => {
    setSaving(true);
    try { await axios.put(`${API}/api/notifications/preferences`, { client_updates: e.target.checked }, { headers }); toast('Preferences saved.'); reload(); }
    catch (err) { toast(errText(err)); } finally { setSaving(false); }
  };
  return (
    <section className="panel"><h2>Service updates</h2><p>Manage appointment and account update preferences.</p>
      <label className="check"><input type="checkbox" checked={prefs ? prefs.preferences.client_updates !== false : true} disabled={!prefs || saving} onChange={toggle} data-testid="pref-portal-updates" />
        <span>Portal updates<small>Replies from your team, request decisions and new quotes in your notifications.</small></span></label>
      <label className="check"><input type="checkbox" checked disabled readOnly />
        <span>Appointment emails<small>Booking confirmations, reminders and receipts are sent to your account email.</small></span></label>
      <p className="muted">Text messages need your separate consent. Ask your team if you’d like appointment texts.</p>
    </section>
  );
};

export default function ClientAccountPage() {
  const { logout } = useAuth();
  const { openDrawer } = usePortal();
  const [me, reload, err] = useLoad('/api/account/me');
  const [props] = useLoad('/api/clients/me/addresses');
  const head = <Welcome title="My Account" desc="Your contact details and communication preferences." />;
  if (err && !me) return <>{head}<LoadError text={err} onRetry={reload} /></>;
  if (!me) return <>{head}<Loading /></>;
  return (
    <div data-testid="client-account">
      <PhotoPanel />
      {head}
      <div className="account-grid">
        <DetailsForm me={me} onSaved={reload} />
        <div>
          <UpdatesPanel />
          <section className="panel" style={{ marginTop: 22 }}><h2>Security & access</h2><p>Sign-in and password recovery use your secure Gleam Guru account.</p>
            <div className="inline-actions">
              <button className="text-button" onClick={() => openDrawer({ title: 'Change password', label: 'SECURITY & ACCESS', content: <PasswordForm /> })} data-testid="account-password">Change password ↗</button>
              <button className="text-button" onClick={logout} data-testid="account-logout">Sign out</button>
            </div></section>
        </div>
      </div>
      <PortalContacts properties={props || []} />
    </div>
  );
}
